import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, WrappedComponentProps as WithIntl } from 'react-intl';

import { type Team, SYNC_STATUS } from '@yandex-int/k-common/typings';
import b from 'b_';
import cx from 'classnames';
import { TableRow, TableCell, Text, POPUP_AXIS, POPUP_DIRECTIONS, POPUP_THEMES, Popup } from 'platform-components';
import { withRouterPath, InjectedProps as WithRouterPath } from 'platform-components/hocs';
import { getExactPathMatch } from 'platform-components/utils';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import ClassAvatar from 'common.components/class-avatar/class-avatar';
import { isMesTeam } from 'common.components/classes/utils';
import ExternalLmsBadge from 'common.components/external-lms-badge/external-lms-badge';
import { getCanEditClass } from 'common.components/user/selectors';
import { getTeamSchoolName, getTeamName } from 'utils/get-name';

// @ts-ignore js-file
import ClassMenu from '../__class-menu/class__class-menu';
import { ReduxTeacherStore } from '../../../typings';
import { MenuOption } from '../typings';

import './class__class-row.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;

interface OwnProps extends WithRouterPath, WithIntl {
    menuOptions: Array<MenuOption>;
    value: Team;
}

interface Props extends StoreProps, OwnProps {}

class ClassRowComponent extends React.Component<Props> {
    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };

    constructor(props: Props) {
        super(props);

        this.state = { hovered: false, ownerSet: false };
    }

    state = {
        hovered: false,
        ownerSet: false,
    };

    componentDidUpdate() {
        if (!this.state.ownerSet) {
            this.popupRef.current?.setOwner(this.rowRef.current);

            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ ownerSet: true });
        }
    }
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    onHover = () => {
        this.setState({ hovered: true });
    };

    onHoverEnd = () => {
        this.setState({ hovered: false });
    };

    rowRef = React.createRef<HTMLDivElement>();
    popupRef = React.createRef<Popup>();

    // eslint-disable-next-line complexity
    render() {
        const {
            menuOptions,
            value,
            getRouterPath,
            isClassSelected,
            location: { pathname },
            canEdit,
            teamSchoolName,
            intl,
        } = this.props;

        const {
            id,
            is_supervisor: isSupervisor,
            level,
            name,
            school_name: schoolName,
            sync_status: syncStatus,
        } = value;

        const { staticUrls } = this.context;

        const match = getExactPathMatch(staticUrls, pathname);
        const params = match?.params;
        let path = match?.path || '';

        if (path === staticUrls.labClasses) {
            path = staticUrls.labClassStudents;
        }

        const syncing = syncStatus === SYNC_STATUS.FIRST_SYNC;

        const isMes = isMesTeam(value);

        const teamName = getTeamName({
            grade: value.level,
            name: value.name,
            jsx: true,
            jsxBoldGrade: true,
            teamLabelCase: 'им',
            hideCollegeLabel: true,
            capitalizeTeamLabel: true,
            e2eIds: { grade: 'qa_class-row-grade', name: 'qa_class-row-team-name' },
            intl,
        });

        return (
            <TableRow
                contentMix="class__class-row-content"
                disabled={syncing}
                linkTo={getRouterPath(path, { ...params, classId: value.id })}
                mix={cx(b('class', 'class-row', { selected: isClassSelected, syncing }), 'qa_teams-list-item')}
                onHover={this.onHover}
                onHoverEnd={this.onHoverEnd}
            >
                {isMes && <ExternalLmsBadge mix="class__class-row-badge" textId="class.badge.mes" />}
                <TableCell mix="class__class-row-cell">
                    <ClassAvatar
                        id={id}
                        level={level}
                        mix="class__class-row-avatar"
                        syncing={syncing}
                        withStar={isSupervisor}
                    />
                    <div className={b('class', 'class-info', { syncing })} ref={this.rowRef}>
                        <div className="class__class-row-name">{teamName}</div>
                        <div className="class__class-row-school">{teamSchoolName}</div>

                        {isMes && !syncing && (
                            <Popup
                                axis={POPUP_AXIS.END}
                                direction={POPUP_DIRECTIONS.TOP_RIGHT}
                                offsetLeft={-50}
                                offsetTop={-60}
                                ref={this.popupRef}
                                shown={this.state.hovered}
                                theme={POPUP_THEMES.DARK}
                            >
                                <Text className="class__class-popup-level">{level}</Text>«<Text>{name}</Text>»
                                <Text className="class__class-popup-text" id="class.classesTable.label" />
                                <br />
                                <br />
                                {schoolName}
                            </Popup>
                        )}
                    </div>
                </TableCell>
                <TableCell>
                    {canEdit && !syncing && <ClassMenu classItem={value} menuOptions={menuOptions} />}
                </TableCell>
            </TableRow>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: OwnProps) => {
    const {
        value,
        match: { params },
    } = ownProps;
    const classId = Number(value.id);
    const selectedClassId = Number(params?.classId) || null;

    return {
        isClassSelected: classId === selectedClassId,
        canEdit: getCanEditClass(storeState, value.id),

        teamSchoolName: getTeamSchoolName({
            grade: value.level,
            schoolName: value.school_name,
        }),
    } as const;
};

export const ClassRow = compose<any>(injectIntl, withRouterPath, connect(mapStateToProps))(ClassRowComponent);
